/**
 * conversationActions.js
 * Talkroute
 *
 * Created by Vel Genov on 3/13/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import moment from "moment";

import uiString from "@constants/uiString";

import {
  NEW_CONVERSATION_ID,
  createWelcomeConversation,
  WELCOME_CONVERSATION,
  ConversationData,
} from "@services/dataClasses/ConversationData";
import { getTopRelatedCalls } from "@services/dataClasses/CallData";
import apiServiceInstance from "@services/TalkrouteAPIService";

import validateArray from "@helpers/arrayValidator";
import FilePathHelper from "@helpers/filePathHelper";

import { appConfig } from "../index";
import { localStorageItems } from "@helpers/hooks/useLocalStorage.hook";
import { IUserMetadata } from "@models/LocalStorageItems.models";
import { RootState } from "@helpers/store/store";


export const FETCH_CONVERSATIONS_BEGIN = "FETCH_CONVERSATIONS_BEGIN";
export const FETCH_FILTERED_CONVERSATIONS_BEGIN = "FETCH_FILTERED_CONVERSATIONS_BEGIN";
export const FETCH_FILTERED_CONVERSATIONS_BATCHES_BEGIN = "FETCH_FILTERED_CONVERSATIONS_BATCHES_BEGIN";
export const FETCH_CONVERSATIONS_BATCHES_BEGIN = "FETCH_CONVERSATIONS_BATCHES_BEGIN";
export const FETCH_CONVERSATIONS_SUCCESS = "FETCH_CONVERSATIONS_SUCCESS";
export const FETCH_FILTERED_CONVERSATIONS_SUCCESS = "FETCH_FILTERED_CONVERSATIONS_SUCCESS";
export const FETCH_CONVERSATIONS_BATCHES_SUCCESS = "FETCH_CONVERSATIONS_BATCHES_SUCCESS";
export const FETCH_FILTERED_CONVERSATIONS_BATCHES_SUCCESS = "FETCH_FILTERED_CONVERSATIONS_BATCHES_SUCCESS";
export const FETCH_NEW_CONVERSATIONS_SUCCESS = "FETCH_NEW_CONVERSATIONS_SUCCESS";
export const FETCH_CONVERSATIONS_FAILURE = "FETCH_CONVERSATIONS_FAILURE";
export const FETCH_FILTERED_CONVERSATIONS_FAILURE = "FETCH_FILTERED_CONVERSATIONS_FAILURE";
export const FETCH_CONVERSATIONS_BATCHES_FAILURE = "FETCH_CONVERSATIONS_BATCHES_FAILURE";
export const FETCH_FILTERED_CONVERSATIONS_BATCHES_FAILURE = "FETCH_FILTERED_CONVERSATIONS_BATCHES_FAILURE";
export const FETCH_MESSAGES_SUCCESS = "FETCH_MESSAGES_SUCCESS";
export const FETCH_MESSAGES_STARTED = "FETCH_MESSAGES_STARTED";
export const FETCH_MESSAGES_ENDED = "FETCH_MESSAGES_ENDED";
export const NO_NEW_MESSAGES = "NO_NEW_MESSAGES";
export const UPDATE_CONVERSATION_READ_STATUS =
  "UPDATE_CONVERSATION_READ_STATUS";
export const DELETE_CONVERSATION_SUCCESS = "DELETE_CONVERSATION_SUCCESS";
export const APPLY_CONVERSATION_FILTER = "APPLY_CONVERSATION_FILTER";
export const SHOW_CONVERSATION_DETAILS = "SHOW_CONVERSATION_DETAILS";
export const HIDE_CONVERSATION_DETAILS = "HIDE_CONVERSATION_DETAILS";
export const SELECT_CONVERSATION = "SELECT_CONVERSATION";
export const MULTIPLE_SELECTED_CONVERSATIONS = "MULTIPLE_SELECTED_CONVERSATIONS";
export const CLOSE_CONVERSATION = "CLOSE_CONVERSATION";
export const CREATE_NEW_CONVERSATION = "CREATE_NEW_CONVERSATION";
export const DELETE_NEW_CONVERSATION = "DELETE_NEW_CONVERSATION";
export const UPDATE_CONVERSATION_NUMBER = "UPDATE_CONVERSATION_NUMBER";
export const REMOVE_CONVERSATION_NUMBER = "REMOVE_CONVERSATION_NUMBER";
export const UPDATE_CONVERSATION_FROM_NUMBER =
  "UPDATE_CONVERSATION_FROM_NUMBER";
export const SEND_MESSAGE_STARTED = "SEND_MESSAGE_STARTED";
export const SEND_MESSAGE_SUCCESS = "SEND_MESSAGE_SUCCESS";
export const SEND_MESSAGE_FAILURE = "SEND_MESSAGE_FAILURE";
export const FETCH_ATTACHMENT_SUCCESS = "FETCH_ATTACHMENT_SUCCESS";
export const FETCH_ATTACHMENT_ERROR = "FETCH_ATTACHMENT_ERROR";
export const SET_IS_CONVERSATION_SELECTED = "SET_IS_CONVERSATION_SELECTED";
export const SWITCH_CONVERSATION_MULTIPLE_SELECTION = "SWITCH_CONVERSATION_MULTIPLE_SELECTION";
export const MULTIPLE_SELECTION_TYPES = {
  SINGLE: "SINGLE",
  RANGE: "RANGE",
  ALL: "ALL",
  NONE: "NONE"
}
export const SET_TEMPORARY_CONVERSATION = "SET_TEMPORARY_CONVERSATION";
/**
 * Fetches the conversations from the API service
 *
 * @param since an optioanal since parameter. Fetch only conversation since this time
 * @param offset an optioanl offset parameter. Fetch the next conversation limit after the offset
 * @param showLoading show the loading state while fetching. Default is true
 * @param clearDraft clear any draft conversations on a successful fetch. Default is false
 */
export const fetchConversations = (
  since,
  offset,
  showLoading = true,
  clearDraft = false
) => {
  return (dispatch, getState: () => RootState) => {
    if (showLoading === true) {
      dispatch(fetchConversationsBegin());
    }


    return apiServiceInstance
    .getConversations({ since, offset })
    .then((data) => {
      const { conversations, pagination } = data;
      let sortedData = sortConversations(conversations);
      // const contactList = getState().contactsReducer.data;
      //   if (contactList.length) { 
      //     sortedData = addContactNameToConversation(contactList, sortedData);
      //   } else {
      //     dispatch(mergeContactsToConversations(true));
      //   }
        dispatch(
          fetchConversationsSuccess({
            data: sortedData,
            pagination,
            clearDraft,
          })
        );
        return sortedData;
      })
      .catch((error) => dispatch(fetchConversationsFailure(error)));
  };
};

/**
 * Fetches threads from the API service with all the filters
 *
 * @param filterParams Object containing filter parameters and options
 */
export const fetchFilteredConversations = (filterParams) => {
  return async (dispatch, getState: () => RootState) => {
    const {
      offset = null,
      showLoading = true,
      clearDraft = false,
      startDateFirst = null,
      endDateFirst = null,
      startDateLast = null,
      endDateLast = null,
      virtualNumbers = null,
      messageCount = null,
      messageCountOperator = null,
    } = filterParams;

    if (showLoading) {
      dispatch(fetchFilteredConversationsBegin());
    }

    try {
      const queryParams = {
        offset: offset,
        firstMessageAfter: startDateFirst ? startDateFirst.getTime() / 1000 : undefined,
        firstMessageBefore: endDateFirst ? endDateFirst.getTime() / 1000 : undefined,
        since: startDateLast ? startDateLast.getTime() / 1000 : undefined,
        before: endDateLast ? endDateLast.getTime() / 1000 : undefined,
        minMessagesCount: messageCountOperator && messageCountOperator === 'greater' ? messageCount : undefined,
        maxMessagesCount: messageCountOperator && messageCountOperator == 'less' ? messageCount : undefined,
        talkrouteNumber: virtualNumbers == "all" || virtualNumbers == "All Messages" ? null : virtualNumbers,
      };

      const data = await apiServiceInstance.getConversations(queryParams);
      const { conversations, pagination } = data;
      let sortedData = sortConversations(conversations);

      dispatch(fetchFilteredConversationsSuccess({
        data: sortedData,
        pagination,
        clearDraft,
      }));
      dispatch(fetchApplyFilterSuccess(sortedData, filterParams));
    } catch (error) {
      dispatch(fetchFilteredConversationsFailure(error));
    }
  };
};

/**
 * Fetches the conversations from the API service for pagination only
 *
 * @param since an optioanal since parameter. Fetch only conversation since this time
 * @param offset an optioanl offset parameter. Fetch the next conversation limit after the offset
 * @param showLoading show the loading state while fetching. Default is true
 * @param clearDraft clear any draft conversations on a successful fetch. Default is false
 */
export const fetchConversationsForBatches = (
  since,
  offset,
  showLoading = true,
  clearDraft = false
) => {
  return (dispatch, getState: () => RootState) => {
    if (showLoading === true) {
      dispatch(fetchConversationsBatchesBegin());
    }
    
    return apiServiceInstance
    .getConversations({ since, offset })
    .then((data) => {
      const { conversations, pagination } = data;
      let sortedData = sortConversations(conversations);
        dispatch(
          fetchConversationsBatchesSuccess({
            data: sortedData,
            pagination,
            clearDraft,
          })
        );
        return sortedData;
      })
      .catch((error) => dispatch(fetchConversationsBatchesFailure(error)));
  };
};

/**
 * Fetches FILTERED conversations from the API service for pagination only
 *
* @param filterParams Object containing filter parameters and options
 */
export const fetchConversationsForBatchesFiltered = (filterParams) => {
  return (dispatch, getState: () => RootState) => {
    const {
      offset = null,
      showLoading = true,
      clearDraft = false,
      startDateFirst = null,
      endDateFirst = null,
      startDateLast = null,
      endDateLast = null,
      virtualNumbers = null,
      messageCount = null,
      messageCountOperator = null,
    } = filterParams;

    const queryParams = {
      offset: offset,
      firstMessageAfter: startDateFirst ? startDateFirst.getTime() / 1000 : undefined,
      firstMessageBefore: endDateFirst ? endDateFirst.getTime() / 1000 : undefined,
      since: startDateLast ? startDateLast.getTime() / 1000 : undefined,
      before: endDateLast ? endDateLast.getTime() / 1000 : undefined,
      minMessagesCount: messageCountOperator && messageCountOperator === 'greater' ? messageCount : undefined,
      maxMessagesCount: messageCountOperator && messageCountOperator == 'less' ? messageCount : undefined,
      talkrouteNumber: virtualNumbers == "all" || virtualNumbers == "All Messages" ? null : virtualNumbers,
    };
    
    return apiServiceInstance
    .getConversations(queryParams)
    .then((data) => {
      const { conversations, pagination } = data;
      let sortedData = sortConversations(conversations);
        dispatch(
          fetchFilteredConversationsBatchesSuccess({
            data: sortedData,
            pagination,
            clearDraft,
          })
        );
        return sortedData;
      })
      .catch((error) => dispatch(fetchFilteredConversationsBatchesFailure(error)));
  };
};

/**
 * Used to fetch conversations during an initial load. Clears all existing conversation caches
 * Currently loads conversations up to the 1st limit
 */
export const fetchAllConversations = () => {
  return (dispatch, getState: () => RootState) =>
    apiServiceInstance
      .getConversations({})
      .then((data: any) => {
        const { conversations, pagination } = data;
        let updatedConversations = validateArray(conversations)
          ? conversations
          : [];

        // If the welcome conversation exists, add it back to the new fetch batch
        const { data: existingConversations } = getState().conversations;
        const welcomeConversationIndex = findConversationById({
          conversationId: WELCOME_CONVERSATION,
          conversations: existingConversations,
        });
        const {
          userId
        } = getState().authentication;
        const metadata = window.localStorage.getItem(localStorageItems.metadata);
        let creatingWelcomeConversationAllowed = true;
        let isWelcomeConversationRead = false;
        if (metadata) {
          const userData: IUserMetadata = JSON.parse(metadata).find(_userData => _userData.userId === userId);
          creatingWelcomeConversationAllowed = userData ? !userData.isWelcomeConversationDeleted : true;
          isWelcomeConversationRead = userData ? userData.isWelcomeConversationRead : false;
        }
        
        // If this is a new login, add the welcome conversation
        const { newLogin } = getState().authentication;
        if (newLogin === true && creatingWelcomeConversationAllowed) {
          // Add a new welcome conversation
          const welcomeConversation = createWelcomeConversation(isWelcomeConversationRead);
          updatedConversations = [welcomeConversation, ...updatedConversations];
        } else if (welcomeConversationIndex >= 0) {
          // If the welcome conversation exists, re-add it
          if (validateArray(existingConversations)) {
            const existingWelcomeConversation =
              existingConversations[welcomeConversationIndex];
            updatedConversations = [
              existingWelcomeConversation,
              ...updatedConversations,
            ];
          }
        }

        let  sortedData = sortConversations(updatedConversations);
        // const contactList = getState().contactsReducer.data;
        // if (contactList.length) { 
        //   sortedData = addContactNameToConversation(contactList, sortedData);
        // } else {
        //   dispatch(mergeContactsToConversations(true));
        // }
        dispatch(
          fetchNewConversationsSuccess({
            data: sortedData,
            pagination,
          })
        );

        return sortedData;
      })
      .catch((error) => dispatch(fetchConversationsFailure(error)));
};

/**
 * Fetch new conversations since the last fetch timestamp
 *
 * @param {*} clearDraft clear any draft conversations on a successful fetch. Default is false
 */
export const fetchNewConversations = (clearDraft = false) => {
  return (dispatch, getState) => {
    const { conversationPagination } = getState().conversations;
    const { timestamp } = conversationPagination ?? { timestamp: null };
    dispatch(fetchConversations(timestamp, null, false, clearDraft));
  };
};

/**
 * Fetch the next batch of conversation using the last known pagination information as reference
 */
export const fetchNextBatchConversations = () => {
  return (dispatch, getState) => {
    const { conversationPagination, totalConversationsOnServer, runningOffset } = getState().conversations;
    if (conversationPagination) {
      const { limit } = conversationPagination;
      const nextOffset = runningOffset + limit;

      if (nextOffset < totalConversationsOnServer) {
        dispatch(fetchConversationsForBatches(null, nextOffset, false));
      }
    }
  };
};

/**
 * Fetch the next batch of FILTERED conversation using the last known pagination information as reference
 */
export const fetchNextBatchFilteredConversations = () => {
  return (dispatch, getState) => {
    const { conversationPagination, totalConversationsOnServer, runningOffset, filterBy } = getState().conversations;
    if (conversationPagination) {
      const { limit } = conversationPagination;
      const nextOffset = runningOffset + limit;

      // this is only going to work for talkroute numbers for now
      const filterParams = {
        virtualNumbers: filterBy,
        offset: nextOffset,
      };

      if (nextOffset < totalConversationsOnServer) {
        dispatch(fetchConversationsForBatchesFiltered(filterParams));
      }
    }
  };
};

/**
 * Delete a conversation from the remote service, and then locally
 *
 * @param {*} conversationId the ID of the Conversation to delete
 */
export const deleteConversation = (conversationId) => {

  return (dispatch, getState) => {
    // If this is the welcome conversation skip the API call
    if (conversationId === WELCOME_CONVERSATION) {
      dispatch(deleteConversationSuccess(conversationId));
      return conversationId;
    }

    // Get the timestamp of the conversation's last message
    const { data: conversations } = getState().conversations;

    const conversationIndex = findConversationById({
      conversationId,
      conversations,
    });

    let since = moment().utcOffset(0).unix();

    const conversation = conversations[conversationIndex];
    if (conversation) {
      since = conversation.lastMessage.createdAtMicroseconds;
    }

    return apiServiceInstance
      .deleteConversation({ conversationId, since })
      .then(() => {
        dispatch(deleteConversationSuccess(conversationId));
        cleanUpConversationFiles(conversationId);
        return conversationId;
      })
      .catch((error) => dispatch(fetchConversationsFailure(error)));
  };
};

/**
 * Download a conversation from the remote service
 *
 * @param {*} conversationId the ID of the Conversation to download
 */
export const getConversationDownload = (conversationId) => {
  return apiServiceInstance
    .getMessagesDownload({ conversationId })
    .catch(error => {
      console.error('Error downloading CSV:', error);
      throw error;
    });
}

/**
 * Clean up local files for a conversation identified by a conversation Id
 *
 * @param {*} conversationId
 */
const cleanUpConversationFiles = (conversationId) => {
  const messageAttachmentFilePath = new FilePathHelper().getMessageAttachmentFilePath(
    conversationId
  );

  if (messageAttachmentFilePath) {
    try {
      appConfig.fileStorage?.remove(messageAttachmentFilePath);
    } catch (error) {
    }
  }
};

/**
 * Update the read status of a conversation with the remote service, and then locally
 *
 * @param {*} conversationId
 * @param {*} readStatus
 */
export const updateReadStatus = (props) => {
  const { conversationId, readStatus } = props;
  return (dispatch, getState) => {
    // If this is the welcome conversation skip the API call
    if (conversationId === WELCOME_CONVERSATION) {
      dispatch(updateReadStatusSuccess(conversationId, readStatus));
      return { conversationId, readStatus };
    }

    // Get the timestamp of the conversation's last message
    const { data: conversations } = getState().conversations;

    const conversationIndex = findConversationById({
      conversationId,
      conversations,
    });

    let since = moment().utcOffset(0).unix();

    const conversation = conversations[conversationIndex];
    if (conversation) {
      since = conversation.lastMessage.createdAtMicroseconds;
    }

    return apiServiceInstance
      .markConversationAsRead({ conversationId, readStatus, since })
      .then(() => {
        dispatch(updateReadStatusSuccess(conversationId, readStatus));
        return { conversationId, readStatus };
      })
      .catch((error) => dispatch(fetchConversationsFailure(error)));
  };
};

/**
 * An action to apply a filter to the passed in conversations
 * @param {*} filterBy the phone number to filter by
 */
export const applyConversationFilter = (filterBy) => {
  const queryParams = {
    virtualNumbers: filterBy,
  }
  return (dispatch, getState) => {
    dispatch(fetchFilteredConversations(queryParams));
  };
};

/**
 * Show details about a conversation. Also finds up to callHistoryActions/TOP_CALLS_NUMBER related calls in the call history
 * @param {*} callHistory An array containing call history data to look for related calls in
 * @param {*} conversation The conversation to show details for
 * @param {*} parent the parent component that we are showing call details about. Usually TextMessages
 */
export const showConversationDetails = (callHistory, conversation, parent) => {
  const { conversationId } = conversation;

  return {
    type: SHOW_CONVERSATION_DETAILS,
    payload: {
      conversationId,
      topRelatedCalls: getTopRelatedCalls(callHistory, conversation),
      parent,
    },
  };
};

/**
 * Hide details about a conversation.
 * @param {*} parent the parent component that we are showing conversation details about. Usually TextMessages
 */
export const hideConversationDetails = (parent) => ({
  type: HIDE_CONVERSATION_DETAILS,
  payload: {
    conversation: null,
    topRelatedCalls: [],
    parent,
  },
});

/**
 *
 * @param conversations the array of conversations to search in
 * @param conversationId the ID of the conversation to search for
 *
 * @returns either a found conversation or undefined
 */
export const findConversationById = (props) => {
  const { conversations, conversationId } = props;

  if (validateArray(conversations)) {
    const foundIndex = conversations.findIndex(
      (conversationEntry) => conversationEntry.conversationId === conversationId
    );

    return foundIndex;
  }

  return undefined;
};

/**
 * Count the number of unread conversations in the passed in parameter
 *
 * @param {*} conversations the conversations to check for any unread ones
 * @param selectedConversationId the ID of selected conversation
 * @param isConversationSelected wheter the conversation tab is selected or not
 * @return the number of unread conversations
 */
export const countUnreadConversations = (
  conversations,
  selectedConversationId,
  isConversationSelected
) => {
  const defaultUnreadCount = 0;

  if (validateArray(conversations)) {
    // Count the number of isRead === false occurances in the last message of each conversation
    // Assumes each conversation must have a lastMessage parameter
    const unreadCount = conversations.reduce(
      (accumulator, currentConversation) => {
        if (isConversationSelected) {
          if (currentConversation.conversationId !== selectedConversationId) {
            const { isRead } = currentConversation.lastMessage;
            if (isRead === false) {
              return accumulator + 1;
            }
          }
        } else {
          const { isRead } = currentConversation.lastMessage;
          if (isRead === false) {
            return accumulator + 1;
          }
        }

        return accumulator;
      },
      defaultUnreadCount
    );

    return unreadCount;
  }

  return defaultUnreadCount;
};

/**
 * Creates a new conversation draft with a talkrouteNumber
 *
 * @param {*} talkrouteNumber the talkroute number to use with the new conversation
 */
export const createNewConversation = (talkrouteNumber: string) => ({
  type: CREATE_NEW_CONVERSATION,
  payload: { talkrouteNumber },
});

/**
 * Deletes the new conversation draft
 */
export const deleteNewConversation = () => ({
  type: DELETE_NEW_CONVERSATION,
  payload: {},
});

/**
 * Remove the contactNumber of conversation draft
 */
export const removeNewConversationPhoneNumber = () => ({
  type: REMOVE_CONVERSATION_NUMBER,
  payload: {},
});

/**
 * Add a contactNumber to a conversation draft
 *
 * @param {*} phoneNumber the contactNumber to use with the conversation
 */
export const updateNewConversationPhoneNumber = (phoneNumber) => ({
  type: UPDATE_CONVERSATION_NUMBER,
  payload: { phoneNumber },
});

/**
 * Add a from phone number to a conversation draft
 *
 * @param {*} fromNumber the outgoing text phone number to use with the conversation
 */
export const updateNewConversationFromPhoneNumber = (fromNumber) => ({
  type: UPDATE_CONVERSATION_FROM_NUMBER,
  payload: { fromNumber },
});

/**
 * Helper function
 * Sorts the passed in array of conversations by the
 * createdAtMicroseconds value of their last messages
 * @param {*} data the conversations to sort
 */
export const sortConversations = (data: ConversationData[]) => {
  const sortedData = [...data];
  if (validateArray(sortedData)) {
    // Sort by date before returning
    sortedData.sort(
      (a, b) =>
        // A simple comparator function that uses the numeric createdAtMicroseconds values for comparison
        b.lastMessage.createdAtMicroseconds -
        a.lastMessage.createdAtMicroseconds
    );
  }

  return sortedData;
};

/**
 * Verify if a message count is available for a conversation. Request an update if not
 *
 * @param {*} conversationId the id of the conversation to update
 * @param {*} conversations the list of conversations
 */
export const updateMessageCount = (props) => {
  const { conversationId, conversations } = props;

  const conversationIndex = findConversationById({
    conversationId,
    conversations,
  });
  const conversation = conversations[conversationIndex];
  if (conversation !== undefined) {
    const { messagesOnServer } = conversation;
    if (messagesOnServer == null) {
      return getAllMessagesSinceLast({ conversationId, conversations });
    }
  }

  return { type: NO_NEW_MESSAGES };
};

/**
 * Checks the lastMessageFetch parameter on a conversation and fetches all new messages since then
 *
 * @param {*} conversationId the ID of the conversation we are fetching messages for
 * @param {*} conversations an array of existing conversations
 */
export const getAllMessagesSinceLast = (props) => {
  const { conversationId, conversations } = props;

  const conversationIndex = findConversationById({
    conversationId,
    conversations,
  })
  const conversation = conversations[conversationIndex];
  if (conversation !== undefined) {
    // If the conversation exists, proceed
    const { lastMessageFetch } = conversation;
    return getMessages({ conversationId, since: lastMessageFetch });
  }
  return { type: NO_NEW_MESSAGES };
};

/**
 * Checks if all messages have been fetched, and fetches the next batch if there are more messages to fetch
 * The size of the next batch is determined by the limit in the pagination information
 *
 * @param {*} conversationId the ID of the conversation we are fetching messages for
 * @param {*} conversations an array of existing conversations
 */
export const getNextBacthMessages = (props) => {
  const { conversationId, conversations } = props;

  const conversationIndex = findConversationById({
    conversationId,
    conversations,
  });

  const conversation = conversations[conversationIndex];
  if (conversation !== undefined) {
    // If the conversation exists, proceed
    const localMessagesCount = conversation.getLocalMessagesCount();
    const { messagesOnServer } = conversation;
    if (messagesOnServer > localMessagesCount || messagesOnServer === null) {
      // Do a fetch with localMessagesCount as the offset
      return getMessages({
        conversationId,
        since: null,
        offset: localMessagesCount,
      });
    }
  }
  return { type: NO_NEW_MESSAGES };
};

/**
 * Get messages with the passed in parameters, and send a success action when the messages have been fetched
 *
 * @param {*} conversationId the conversation to fetch messages for
 * @param {*} since the timestamp to fetch messages since
 * @param {*} offset the offset to use when fetching
 */
const getMessages = (props) => {
  const { conversationId, since, offset } = props;

  if (conversationId === WELCOME_CONVERSATION) {
    return { type: NO_NEW_MESSAGES };
  }

  return (dispatch) => {
    dispatch(fetchMessagesStarted());
    apiServiceInstance
      .getMessages({ conversationId, since, offset })
      .then((data: any) => {
        const { messages, pagination } = data;
        if (pagination.count === 0) {
          // There are no messages. This conversation has been deleted
          const messageNotAvailableError = new Error(
            uiString.MESSAGE_NOT_AVAILABLE
          );
          dispatch(fetchConversationsFailure(messageNotAvailableError));
          dispatch(deleteConversationSuccess(conversationId));
          dispatch(fetchMessagesEnded());

          return data;
        }
        dispatch(fetchMessagesSuccess(messages, pagination, conversationId));

        return data;
      })
      .catch((error) => {
        const { message } = error;
        if (message === "Not Found") {
          dispatch(deleteConversationSuccess(conversationId));
        }

        dispatch(fetchConversationsFailure(error));
        dispatch(fetchMessagesEnded());
      });
  };
};

/**
 * Get message attachment
 *
 * @param {*} attachmentId the id of the attachment
 * @param {*} messageId the message containing the attachment
 * @param {*} conversationId the conversation containing the message
 */
export const getMessageAttachment = (props) => {
  const { messageId, attachmentId, conversationId } = props;
  return (dispatch) =>
    apiServiceInstance
      .getAttachmentFile({ messageId, attachmentId, conversationId })
      .then((filePath) => {
        dispatch(
          fetchAttachmentSuccess(
            filePath,
            messageId,
            conversationId,
            attachmentId
          )
        );
        return attachmentId;
      })
      .catch((error) => {
        dispatch(fetchAttachmentError(error, attachmentId));
      });
};

/**
 * Send a text message, and send a success action when the message has been sent
 *
 * @param {*} conversationId the conversation to fetch messages for
 * @param {*} messageBody the text message body to send
 */
export const sendMessage = (props) => {
  const { messageBody, files } = props;
  let { conversationId } = props;

  return (dispatch, getState) => {
    dispatch(sendMessageStarted());
    if (conversationId === NEW_CONVERSATION_ID) {
      const { conversationDraft } = getState().conversations;

      if (conversationDraft) {
        conversationId = conversationDraft.generateConversationId();

        return apiServiceInstance
          .sendMessage({ conversationId, messageBody, files })
          .then((data) => {
            dispatch(fetchNewConversations(true));
            dispatch(sendMessageSuccess());
            return data;
          })
          .catch((error) => {
            dispatch(sendMessageFailure(error));
            dispatch(fetchConversationsFailure(error));
          });
      }
    }

    return apiServiceInstance
      .sendMessage({ conversationId, messageBody, files })
      .then((data: any) => {
        const { message } = data;

        // Use the actual message pagination info when switching over the the real API
        const pagination = {
          count: 0,
          timestamp: message.createdAtMicroseconds,
        };

        dispatch(sendMessageSuccess(message, pagination, conversationId));
        return data;
      })
      .catch((error) => {
        dispatch(sendMessageFailure(error));
        dispatch(fetchConversationsFailure(error));
      });
  };
};

/**
 * Create an action to select a conversation
 *
 * @param {*} conversationId the ID of the conversation to select
 */
export const selectConversation = (conversationId) => ({
  type: SELECT_CONVERSATION,
  payload: { conversationId },
});

/**
 * Create an action to close a currently open conversation
 *
 */
export const closeConversation = () => ({
  type: CLOSE_CONVERSATION,
});

/**
 * Create an action to select one of many conversations
 *
 * @param {*} conversationId the ID of the conversation to select
 */
export const selectMultipleConversation = (conversationId: string, type: string) => ({
  type: MULTIPLE_SELECTED_CONVERSATIONS,
  payload: { conversationId, type }
});

/**
 * Switch multiple conversation edit mode
 *
 * @param {*} selectionMode boolean to turn on/off the multiple selection mode
 */
export const switchConversationSelectionMode = (multipleSelectionMode: boolean) => ({
  type: SWITCH_CONVERSATION_MULTIPLE_SELECTION,
  payload: { multipleSelectionMode }
});

/**
 * Start fething the conversations
 */
export const fetchConversationsBegin = () => ({
  type: FETCH_CONVERSATIONS_BEGIN,
});

/**
 * Start fething FILTERED conversations
 */
export const fetchFilteredConversationsBegin = () => ({
  type: FETCH_FILTERED_CONVERSATIONS_BEGIN,
});

/**
 * Start fething the conversations
 */
export const fetchConversationsBatchesBegin = () => ({
  type: FETCH_CONVERSATIONS_BATCHES_BEGIN,
});

/**
 * Start fething the FILTERED conversations
 */
export const fetchFilteredConversationsBatchesBegin = () => ({
  type: FETCH_FILTERED_CONVERSATIONS_BATCHES_BEGIN,
});

/**
 * Set Conversation Tab selected or not
 * @param {*} isConversationSelected Conversation Tab selected or not
 */
export const setIsConversationSelected = (props) => ({
  type: SET_IS_CONVERSATION_SELECTED,
  payload: { isConversationSelected: props },
});

/**
 * Successfully fetched the conversations
 * @param {*} conversations the that were fetched
 */
export const fetchConversationsSuccess = (props) => ({
  type: FETCH_CONVERSATIONS_SUCCESS,
  payload: { conversations: props },
});

/**
 * Successfully fetched FILTERED conversations
 * @param {*} conversations the that were fetched
 */
export const fetchFilteredConversationsSuccess = (props) => ({
  type: FETCH_FILTERED_CONVERSATIONS_SUCCESS,
  payload: { conversations: props },
});

export const fetchApplyFilterSuccess = (conversations, filterBy) => {
  // this is a temporary solution. Something else will have to be done if we ever use more of the filters here (right now we only use talkrouteNumber)
  const destructuredFilterBy = filterBy.virtualNumbers;
  return {
    type: APPLY_CONVERSATION_FILTER,
    payload: {conversations, filterBy: destructuredFilterBy}
  }
}

/**
 * Successfully fetched the conversations for batches
 * @param {*} conversations the that were fetched
 */
export const fetchConversationsBatchesSuccess = (props) => ({
  type: FETCH_CONVERSATIONS_BATCHES_SUCCESS,
  payload: { conversations: props },
});

/**
 * Successfully fetched the FILTERED conversations for batches
 * @param {*} conversations the that were fetched
 */
export const fetchFilteredConversationsBatchesSuccess = (props) => ({
  type: FETCH_FILTERED_CONVERSATIONS_BATCHES_SUCCESS,
  payload: { conversations: props },
});

/**
 * Successfully fetched the new list of conversations
 * @param {*} conversations the that were fetched
 */
export const fetchNewConversationsSuccess = (props) => ({
  type: FETCH_NEW_CONVERSATIONS_SUCCESS,
  payload: { conversations: props },
});

/**
 * An error occured while fetching the conversations
 * @param {*} error
 */
export const fetchConversationsFailure = (error) => ({
  type: FETCH_CONVERSATIONS_FAILURE,
  payload: { error },
});

/**
 * An error occured while fetching FILTERED conversations
 * @param {*} error
 */
export const fetchFilteredConversationsFailure = (error) => ({
  type: FETCH_FILTERED_CONVERSATIONS_FAILURE,
  payload: { error },
});

/**
 * An error occured while fetching the conversations
 * @param {*} error
 */
export const fetchConversationsBatchesFailure = (error) => ({
  type: FETCH_CONVERSATIONS_BATCHES_FAILURE,
  payload: { error },
});

/**
 * An error occured while fetching the FILTERED conversations
 * @param {*} error
 */
export const fetchFilteredConversationsBatchesFailure = (error) => ({
  type: FETCH_FILTERED_CONVERSATIONS_BATCHES_FAILURE,
  payload: { error },
});

/**
 * Successfully deleted a conversation
 *
 * @param {*} conversationId the ID of the conversation that was deleted
 */
export const deleteConversationSuccess = (conversationId) => ({
  type: DELETE_CONVERSATION_SUCCESS,
  payload: { conversationId },
});

/**
 * Successfully updated the read status of a conversation
 *
 * @param {*} conversationId the ID of the conversation updated
 * @param {*} readStatus the read statuf of the conversation
 */
export const updateReadStatusSuccess = (conversationId, readStatus) => ({
  type: UPDATE_CONVERSATION_READ_STATUS,
  payload: { conversationId, readStatus },
});

/**
 * Successfully fetched messages for a conversation
 *
 * @param {*} messages the messages fetched
 * @param {*} pagination a pagination object indicating the total messages on the server
 * @param {*} conversationId the conversation ID these messages belong to
 */
export const fetchMessagesSuccess = (messages, pagination, conversationId) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, pagination, conversationId },
});

export const fetchMessagesStarted = () => ({
  type: FETCH_MESSAGES_STARTED,
});

export const fetchMessagesEnded = () => ({
  type: FETCH_MESSAGES_ENDED,
});

export const sendMessageStarted = () => ({
  type: SEND_MESSAGE_STARTED,
});

/**
 * Successfully sent a text message
 *
 * @param {*} message the message sent
 * @param {*} pagination a pagination object indicating the total messages on the server
 * @param {*} conversationId the conversation ID these messages belong to
 */
export const sendMessageSuccess = (
  message?: any,
  pagination?: any,
  conversationId?: number
) => ({
  type: SEND_MESSAGE_SUCCESS,
  payload: { message, pagination, conversationId },
});

/**
 * An error was encountered sending a message
 *
 * @param {*} error the error encountered
 */
export const sendMessageFailure = (error) => ({
  type: SEND_MESSAGE_FAILURE,
  payload: { error },
});

/**
 * An attachment has been successfully fetched
 *
 * @param {*} filePath the attachment file path
 * @param {*} messageId the Id of the message containing the attachment
 * @param {*} conversationId the Id of the conversation containing the attachment
 * @param {*} attachmentId the attachment Id
 */
export const fetchAttachmentSuccess = (
  filePath,
  messageId,
  conversationId,
  attachmentId
) => ({
  type: FETCH_ATTACHMENT_SUCCESS,
  payload: { filePath, messageId, conversationId, attachmentId },
});

/**
 * An error was encountered fetching an attachment
 *
 * @param {*} error the error encountered
 * @param {*} attachmentId the Id of the attachment
 */
export const fetchAttachmentError = (error, attachmentId) => ({
  type: FETCH_ATTACHMENT_ERROR,
  payload: { error, attachmentId },
});

export const setTemporaryConversation = (conversation: ConversationData | null, selectConversation: boolean = true) => ({
  type: SET_TEMPORARY_CONVERSATION,
  payload: { conversation, selectConversation },
});

const conversationActions = {
  fetchConversations,
  fetchAllConversations,
  fetchFilteredConversations,
  fetchNewConversations,
  fetchNextBatchConversations,
  fetchNextBatchFilteredConversations,
  deleteConversation,
  cleanUpConversationFiles,
  updateReadStatus,
  applyConversationFilter,
  showConversationDetails,
  hideConversationDetails,
  findConversationById,
  countUnreadConversations,
  createNewConversation,
  deleteNewConversation,
  removeNewConversationPhoneNumber,
  updateNewConversationPhoneNumber,
  updateNewConversationFromPhoneNumber,
  sortConversations,
  updateMessageCount,
  getAllMessagesSinceLast,
  getNextBacthMessages,
  getMessages,
  getMessageAttachment,
  sendMessage,
  selectConversation,
  closeConversation,
  selectMultipleConversation,
  switchConversationSelectionMode,
  fetchConversationsBegin,
  fetchFilteredConversationsBegin,
  fetchConversationsBatchesBegin,
  fetchFilteredConversationsBatchesBegin,
  setIsConversationSelected,
  fetchConversationsSuccess,
  fetchFilteredConversationsSuccess,
  fetchConversationsBatchesSuccess,
  fetchFilteredConversationsBatchesSuccess,
  fetchNewConversationsSuccess,
  fetchConversationsFailure,
  fetchFilteredConversationsFailure,
  fetchConversationsBatchesFailure,
  fetchFilteredConversationsBatchesFailure,
  deleteConversationSuccess,
  updateReadStatusSuccess,
  fetchMessagesSuccess,
  fetchMessagesStarted,
  fetchMessagesEnded,
  sendMessageStarted,
  sendMessageSuccess,
  sendMessageFailure,
  fetchAttachmentSuccess,
  fetchAttachmentError,
};

export default conversationActions;
